import { useState } from 'react';

import { Trans } from '@lingui/react/macro';
import { Button } from '@repo/videa-components/src/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@repo/videa-components/src/ui/dialog';

import { VideaTypography } from '../../../common-components/elements/VideaTypography/VideaTypography';
import { usePreferencesPracticeUpdate } from '../../../react-query/usePreferences';
import { useGetRecentVisits } from '../../../react-query/useGetRecentVisits';
import { type AdjustmentVersion } from '../../../react-query/useGetSharpenedImages';

import { FilterComparisonPreview } from './FilterComparisonPreview';
import { getFilterLabel } from './filterConstants';
import { ImageCarousel } from './ImageCarousel';
import { VisitSelector } from './VisitSelector';

interface VisitImage {
  imageId: string;
  imageType: string;
  thumbnailUrl: string;
}

// Modal dialog for configuring sharpening filter
export const SharpnessFilterSelectionModal = () => {
  const preferencesUpdate = usePreferencesPracticeUpdate();

  // Fetch recent visits
  const recentVisitsQuery = useGetRecentVisits();

  // Local state for POC
  const [isOpen, setIsOpen] = useState(false);
  const [selectedVisit, setSelectedVisit] = useState<number | undefined>(
    undefined,
  );
  const [selectedPatientId, setSelectedPatientId] = useState<
    string | undefined
  >(undefined);
  const [selectedImageId, setSelectedImageId] = useState<string>('');
  const [leftFilter, setLeftFilter] = useState<AdjustmentVersion>('original');
  const [finalFilter, setFinalFilter] =
    useState<AdjustmentVersion>('7.5.0.enhanced');

  const handleSaveFilter = () => {
    if (finalFilter !== 'original') {
      preferencesUpdate.mutate({
        adjustmentVersion: finalFilter,
      } as any);
    }

    setIsOpen(false);
  };

  const handleVisitChange = (visitId: string) => {
    const patientId =
      recentVisitsQuery.data?.visits?.find(
        (visit) => visit.id === Number(visitId),
      )?.patientVideaId || '';
    setSelectedVisit(Number(visitId));
    setSelectedPatientId(patientId);
    setSelectedImageId('');
  };

  return (
    <>
      <Button variant="outline" onClick={() => setIsOpen(true)}>
        <Trans>Configure Sharpening Filter</Trans>
      </Button>

      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="vai-max-w-6xl vai-max-h-[95vh] vai-overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              <Trans>Image Sharpening Configuration</Trans>
            </DialogTitle>
          </DialogHeader>

          <div className="vai-flex vai-flex-col vai-gap-4 vai-w-full">
            {/* Instructions */}
            <div className="vai-flex vai-flex-col vai-gap-2">
              <VideaTypography
                variant="labelExtrasmall"
                className="vai-text-gray-600"
              >
                <Trans>
                  Select a recent visit, choose an image, and compare different
                  sharpening filters side-by-side to determine your preferred
                  setting.
                </Trans>
              </VideaTypography>
              <div className="vai-p-3 vai-rounded-md vai-bg-orange-50 vai-border vai-border-orange-200 vai-mt-2">
                <VideaTypography
                  variant="labelExtrasmall"
                  className="vai-text-orange-800"
                >
                  <Trans>
                    Note: This update will only affect images taken starting now
                    and will not automatically rerun all historical images. You
                    can manually rerun individual images if needed.
                  </Trans>
                </VideaTypography>
              </div>
            </div>

            {/* Step 1: Visit Selection */}
            <VisitSelector
              recentVisitsQuery={recentVisitsQuery}
              selectedVisit={selectedVisit}
              onVisitChange={handleVisitChange}
            />

            {/* Step 2: Image Carousel (only show if visit selected) */}
            {selectedVisit && selectedPatientId && (
              <ImageCarousel
                visitId={selectedVisit}
                patientId={selectedPatientId}
                selectedImageId={selectedImageId}
                onImageSelect={setSelectedImageId}
              />
            )}

            {/* Step 3: Side-by-Side Filter Comparison (only show if image selected) */}
            {selectedImageId && selectedVisit && selectedPatientId && (
              <FilterComparisonPreview
                leftFilter={leftFilter}
                rightFilter={finalFilter}
                imageId={selectedImageId}
                onLeftFilterChange={setLeftFilter}
                onRightFilterChange={setFinalFilter}
                visitId={selectedVisit}
                patientId={selectedPatientId}
              />
            )}

            {/* Action Buttons */}
            <div className="vai-flex vai-gap-3 vai-justify-end vai-pt-4 vai-border-t">
              <Button variant="outline" onClick={() => setIsOpen(false)}>
                <Trans>Cancel</Trans>
              </Button>
              <Button
                onClick={handleSaveFilter}
                disabled={!selectedImageId || finalFilter === 'original'}
              >
                {finalFilter === 'original' ? (
                  <Trans>Save No Enhancement as Default</Trans>
                ) : (
                  <Trans>Save {getFilterLabel(finalFilter)} as Default</Trans>
                )}
              </Button>
            </div>
            {!selectedImageId && (
              <VideaTypography
                variant="labelExtrasmall"
                className="vai-text-right vai-text-gray-600"
              >
                <Trans>Select a visit and image to continue</Trans>
              </VideaTypography>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
